import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { SupplementCategory } from '../models/master/supplement-category';
import {
  SupplementMainSubCategoryMapping,
  SupplementSubSubCategoryMapping,
} from '../models/master/supplement-category-mapping';

export interface SupplementCategoryPayload {
  id: number;
  name: string;
  isActive: boolean;
}

@Injectable({ providedIn: 'root' })
export class SupplementCategoryService {
  private readonly baseUrl = `${environment.apiUrl}/SupplementCategoryMaster`;

  constructor(private http: HttpClient) { }

  async getMainCategories(activeOnly = false): Promise<SupplementCategory[]> {
    const res: any = await this.http.get(`${this.baseUrl}/GetSupplementMainCategoryMaster/${activeOnly}`).toPromise();
    return this.mapList(res);
  }

  async getSubCategories(activeOnly = false): Promise<SupplementCategory[]> {
    const res: any = await this.http.get(`${this.baseUrl}/GetSupplementSubCategoryMaster/${activeOnly}`).toPromise();
    return this.mapList(res);
  }

  async getSubSubCategories(activeOnly = false): Promise<SupplementCategory[]> {
    const res: any = await this.http.get(`${this.baseUrl}/GetSupplementSubSubCategoryMaster`, { params: { activeOnly } }).toPromise();
    return this.mapList(res);
  }

  /** Load full supplement category tree in one round-trip (client-side cascade). */
  async getCategoryHierarchy(activeOnly = true): Promise<{
    main: SupplementCategory[];
    sub: SupplementCategory[];
    subSub: SupplementCategory[];
  }> {
    const [main, sub, subSub] = await Promise.all([
      this.getMainCategories(activeOnly),
      this.getSubCategories(activeOnly),
      this.getSubSubCategories(activeOnly),
    ]);
    return { main, sub, subSub };
  }

  async addEditMainCategory(dto: SupplementCategoryPayload): Promise<any> {
    return this.http.post(`${this.baseUrl}/AddEditSupplementMainCategoryMaster`, dto).toPromise();
  }

  async addEditSubCategory(dto: SupplementCategoryPayload): Promise<any> {
    return this.http.post(`${this.baseUrl}/AddEditSupplementSubCategoryMaster`, dto).toPromise();
  }

  async addEditSubSubCategory(dto: SupplementCategoryPayload): Promise<any> {
    return this.http.post(`${this.baseUrl}/AddEditSupplementSubSubCategoryMaster`, dto).toPromise();
  }

  async deleteMainCategory(id: string | number): Promise<any> {
    return this.http.get(`${this.baseUrl}/DeleteSupplementMainCategoryMaster/${id}`).toPromise();
  }

  async deleteSubCategory(id: string | number): Promise<any> {
    return this.http.get(`${this.baseUrl}/DeleteSupplementSubCategoryMaster/${id}`).toPromise();
  }

  async deleteSubSubCategory(id: string | number): Promise<any> {
    return this.http.get(`${this.baseUrl}/DeleteSupplementSubSubCategoryMaster/${id}`).toPromise();
  }

  async getMainSubCategoryMappings(): Promise<SupplementMainSubCategoryMapping[]> {
    const res: any = await this.http.get(`${this.baseUrl}/GetMainSubCategoryMappingMaster`).toPromise();
    return this.mapMainSubMappings(res);
  }

  async getMainSubCategoryMappingByMainId(mainCategoryId: number): Promise<SupplementMainSubCategoryMapping[]> {
    const res: any = await this.http.get(`${this.baseUrl}/GetMainSubCategoryMappingMasterById/${mainCategoryId}`).toPromise();
    return this.mapMainSubMappings(res);
  }

  async saveMainSubCategoryMappings(mappings: SupplementMainSubCategoryMapping[]): Promise<any> {
    return this.http.post(`${this.baseUrl}/AddMainSubCategoryMappingMaster`, mappings).toPromise();
  }

  async getSubToSubSubCategoryMappings(): Promise<SupplementSubSubCategoryMapping[]> {
    const res: any = await this.http.get(`${this.baseUrl}/GetSubToSubSubCategoryMappingMaster`).toPromise();
    return this.mapSubSubMappings(res);
  }

  async getSubToSubSubCategoryMappingBySubId(subCategoryId: number): Promise<SupplementSubSubCategoryMapping[]> {
    const res: any = await this.http.get(`${this.baseUrl}/GetSubToSubSubCategoryMappingMasterById/${subCategoryId}`).toPromise();
    return this.mapSubSubMappings(res);
  }

  async saveSubToSubSubCategoryMappings(mappings: SupplementSubSubCategoryMapping[]): Promise<any> {
    return this.http.post(`${this.baseUrl}/AddSubToSubSubCategoryMappingMaster`, mappings).toPromise();
  }

  private mapMainSubMappings(res: any): SupplementMainSubCategoryMapping[] {
    const rows = res?.response ?? res?.Response ?? [];
    return (Array.isArray(rows) ? rows : []).map((row) => ({
      id: Number(row?.id ?? row?.Id ?? 0) || undefined,
      mainCategoryId: Number(row?.mainCategoryId ?? row?.MainCategoryId ?? 0),
      subCategoryId: Number(row?.subCategoryId ?? row?.SubCategoryId ?? 0),
      subCategoryName: row?.subCategoryName ?? row?.SubCategoryName ?? '',
    }));
  }

  private mapSubSubMappings(res: any): SupplementSubSubCategoryMapping[] {
    const rows = res?.response ?? res?.Response ?? [];
    return (Array.isArray(rows) ? rows : []).map((row) => ({
      id: Number(row?.id ?? row?.Id ?? 0) || undefined,
      subCategoryId: Number(row?.subCategoryId ?? row?.SubCategoryId ?? 0),
      subSubCategoryId: Number(row?.subSubCategoryId ?? row?.SubSubCategoryId ?? 0),
      subSubCategoryName: row?.subSubCategoryName ?? row?.SubSubCategoryName ?? '',
    }));
  }

  private mapList(res: any): SupplementCategory[] {
    const rows = res?.response ?? res?.Response ?? [];
    return (Array.isArray(rows) ? rows : []).map((row) => this.mapRow(row));
  }

  private mapRow(row: any): SupplementCategory {
    return {
      id: String(row?.id ?? row?.Id ?? ''),
      name: row?.name ?? row?.Name ?? '',
      isActive: row?.isActive !== false && row?.IsActive !== false,
    };
  }
}
